import React, { useState, useEffect, useCallback, useRef } from "react";
import useDarkMode from '../../hooks/useDarkMode';
import { Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import Select from "react-select";
import swal from "sweetalert";
import useThemeColor from "../../hooks/useThemeColor";
import { SortOrderDropDownSelection } from "../../components/CustomComponents/DropdownSelections/DropDownSelections";
import ColumnSelection from "../../components/CustomComponents/ColumnSelection/ColumnSelection";
import Preloader from "../../components/CustomComponents/preLoad/preLoader";
import DropDownStyles, { DropDownStylesDark } from "../staff/dropdownStyles";
import axiosInstance from "../../config/axiosApi/axiosApi";
import APIs from "../../Constants/apis";
import AddButton from "../../components/CustomComponents/Buttons/AddButton/AddButton";
import showErrorToast, {
  showSuccessToast,
} from "../../components/CustomComponents/toast/toast.js";
import useIsChildBranch from "../../hooks/useIsChildBranch";
import { setVisibleColumns, setSortOrder, setSortBy } from "../../app/itemGridPreferencesSlice";
import { mergeVisibleColumns, needsPreferencesUpdate } from "../../utils/preferencesUtils";
import "./../../components/CustomComponents/Switchery/ToggleSwitch.css";
import "./../order/ManageOrderFilters.css";

const SEARCH_DEBOUNCE_MS = 1000;

const ManageMenus = () => {
  const CompID = useSelector((state) => state.auth.user).userData?.compID;
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [menu, setMenu] = useState([]);
  const [originalMenuCount, setOriginalMenuCount] = useState(0);
  const pageKey = "menus";
  const defaultVisibleColumns = { sortOrder: true, status: true };
  const defaultSortOrder = { value: "ASC", label: "ASC" };
  const defaultSortBy = { value: "Menu_Orderby", label: "Order By" };
  const preferences = useSelector((state) => state.itemGridPreferences[pageKey] || {});
  const visibleColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
  const sortOrder = preferences.sortOrder || defaultSortOrder;
  const sortBy = preferences.sortBy || defaultSortBy;
  const dispatch = useDispatch();

  const [selectedItems, setSelectedItems] = useState(new Set());
  const lastSelectedIndex = useRef(null);

  // Get app settings from Redux
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;

  // Check if dark mode is enabled
  const isDarkMode = useDarkMode();
  const dropdownStyles = isDarkMode ? DropDownStylesDark : DropDownStyles;

  // Get theme color from centralized hook
  const themeColor = useThemeColor();
  const isChildBranch = useIsChildBranch();


  const sortByOptions = [
    { value: "Menu_Orderby", label: "Order By" },
    { value: "Menu_ID", label: "ID" },
    { value: "Menu_Name", label: "Name" },
  ];

  const clearFilters = () => {
    setSearch("");
  };

  const navigate = useNavigate();
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  useEffect(() => {
    if (!preferences.visibleColumns) {
      dispatch(setVisibleColumns({ pageKey, columns: defaultVisibleColumns }));
    } else {
      if (needsPreferencesUpdate(preferences.visibleColumns, defaultVisibleColumns)) {
        const mergedColumns = mergeVisibleColumns(preferences.visibleColumns, defaultVisibleColumns);
        dispatch(setVisibleColumns({ pageKey, columns: mergedColumns }));
      }
    }

    if (!preferences.sortOrder) {
      dispatch(setSortOrder({ pageKey, sortOrder: defaultSortOrder }));
    }
    if (!preferences.sortBy) {
      dispatch(setSortBy({ pageKey, sortBy: defaultSortBy }));
    }
  }, [dispatch, pageKey]);

  const fetchMenuData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_MENU_LIST, {
        params: {
          CompID,
          SortBy: sortBy.value,
          SortOrder: sortOrder.value,
          Search: search?.trim() || "",
        },
      });

      const MenuData = response.data.data || response.data;
      if (Array.isArray(MenuData)) {
        setMenu(MenuData);
        if (!search.trim()) {
          setOriginalMenuCount(MenuData.length);
        }
      } else {
        // Unexpected API response format silently
        setMenu([]);
      }
    } catch (err) {
      // Error fetching menu data silently
      setMenu([]);
      showErrorToast("Failed to fetch menus.");
    } finally {
      setLoading(false);
    }
  }, [search, sortBy, sortOrder, CompID]);

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchMenuData();
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handler);
  }, [fetchMenuData]);

  const handleAddMenu = () => navigate("/Food-menu/addFoodMenu");

  const handleDeleteMenu = async (menuItem) => {
    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete ${menuItem.menu_Name}?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      setLoading(true);
      const response = await axiosInstance.delete(APIs.DELETE_MENU, {
        params: {
          CompID,
          Menu_ID: menuItem.menu_ID,
        },
      });

      if (response.data.status) {
        setMenu((prevMenu) =>
          prevMenu.filter((item) => item.menu_ID !== menuItem.menu_ID)
        );
        showSuccessToast("Menu is Deleted.");
      } else {
        showErrorToast(response.data.message || "Deletion failed");
      }
    } catch (err) {
      // Error in handleDeleteMenu silently
      showErrorToast("Something Went Wrong.");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectItem = (menuId, index, event) => {
    setSelectedItems((prev) => {
      const newSelected = new Set(prev);

      if (event.shiftKey && lastSelectedIndex.current !== null) {
        const start = Math.min(lastSelectedIndex.current, index);
        const end = Math.max(lastSelectedIndex.current, index);
        for (let i = start; i <= end; i++) {
          if (menu[i]?.menu_ID) {
            newSelected.add(menu[i].menu_ID);
          }
        }
      } else {
        if (newSelected.has(menuId)) {
          newSelected.delete(menuId);
        } else {
          newSelected.add(menuId);
        }
        lastSelectedIndex.current = index;
      }

      return newSelected;
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const newSelected = new Set(menu.map((item) => item.menu_ID));
      setSelectedItems(newSelected);
      lastSelectedIndex.current = null;
    } else {
      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
    }
  };

  const handleDeselectAll = () => {
    setSelectedItems(new Set());
    lastSelectedIndex.current = null;
  };

  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) {
      showErrorToast("No menus selected for deletion.");
      return;
    }

    try {
      const confirmDelete = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to delete ${selectedItems.size} selected menus?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmDelete) return;

      setLoading(true);
      await Promise.all(
        Array.from(selectedItems).map((menuId) =>
          axiosInstance.delete(APIs.DELETE_MENU, {
            params: {
              CompID,
              Menu_ID: menuId,
            },
          })
        )
      );

      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
      fetchMenuData();
      showSuccessToast(`${selectedItems.size} menus deleted successfully.`);
    } catch (err) {
      // Error in bulk delete silently
      showErrorToast("Failed to delete selected menus.");
    } finally {
      setLoading(false);
    }
  };

  const handleBulkEdit = () => {
    if (selectedItems.size === 0) {
      showErrorToast("No menus selected for editing.");
      return;
    }

    const selectedMenuIds = Array.from(selectedItems);
    navigate(`/Food-menu/bulkEdit`, { state: { selectedMenuIds } });
  };

  const handleBulkStatusChange = async (publishStatus) => {
    if (selectedItems.size === 0) {
      showErrorToast("No menus selected for status change.");
      return;
    }

    try {
      const confirmStatusChange = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to ${publishStatus ? "publish" : "unpublish"
          } ${selectedItems.size} selected menus?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmStatusChange) return;

      setLoading(true);
      const updatedMenu = menu.map((item) =>
        selectedItems.has(item.menu_ID)
          ? { ...item, menu_Publish: publishStatus }
          : item
      );
      setMenu(updatedMenu);

      await Promise.all(
        Array.from(selectedItems).map((menuId) =>
          axiosInstance.post(APIs.POST_UPDATE_MENU_STATUS, {
            CompID,
            Menu_ID: menuId,
            Menu_Publish: publishStatus,
          })
        )
      );

      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
      showSuccessToast(
        `${selectedItems.size} menus status updated successfully.`
      );
    } catch (error) {
      // Error in bulk status change silently
      showErrorToast("Failed to update selected menus status.");
      fetchMenuData();
      setSelectedItems(new Set());
      lastSelectedIndex.current = null;
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (e, index) => {
    dragItem.current = index;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("application/json", JSON.stringify(index));
    e.currentTarget.style.opacity = "0.5";
  };

  const handleDragEnter = (e, index) => {
    e.preventDefault();
    dragOverItem.current = index;
    e.currentTarget.style.backgroundColor = "#e9ecef";
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.currentTarget.style.backgroundColor = "";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (e) => {
    e.preventDefault();

    const draggedIndex = JSON.parse(e.dataTransfer.getData("application/json"));
    const overIndex = dragOverItem.current;

    if (draggedIndex === overIndex) {
      dragItem.current = null;
      dragOverItem.current = null;
      return;
    }

    let newMenu = [...menu];
    let oldMenu = [...menu];
    const [draggedMenu] = newMenu.splice(draggedIndex, 1);
    newMenu.splice(overIndex, 0, draggedMenu);

    newMenu = newMenu.map((item, idx) => ({
      ...item,
      menu_Orderby: idx + 1,
    }));

    setMenu(newMenu);

    const FilteredData = newMenu.map((item, index) => ({
      Menu_ID: item.menu_ID,
      Menu_Orderby: index + 1,
    }));

    try {
      await axiosInstance.post(APIs.POST_UPDATE_MENU_SORT_ORDER, {
        CompID: CompID,
        Updates: FilteredData,
      });
      showSuccessToast("Menu order updated successfully.");
    } catch (error) {
      // Error updating menu order silently
      showErrorToast("Failed to update menu order.");
      setMenu(oldMenu);
    }

    dragItem.current = null;
    dragOverItem.current = null;
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = "1";
    document.querySelectorAll("tr").forEach((tr) => {
      tr.style.backgroundColor = "";
    });
    dragItem.current = null;
    dragOverItem.current = null;
  };

  const toggleMenuStatus = async (Menu_ID, currentStatus) => {
    try {
      const confirmStatusChange = await swal({
        title: "Are you sure?",
        text: `Are you sure you want to ${currentStatus ? "unpublish" : "publish"
          } this menu?`,
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (!confirmStatusChange) return;

      setLoading(true);
      const updatedMenu = menu.map((item) =>
        item.menu_ID === Menu_ID
          ? { ...item, menu_Publish: !currentStatus }
          : item
      );
      setMenu(updatedMenu);

      await axiosInstance.post(APIs.POST_UPDATE_MENU_STATUS, {
        CompID: CompID,
        Menu_ID: Menu_ID,
        Menu_Publish: !currentStatus,
      });
      showSuccessToast("Menu status updated successfully.");
    } catch (error) {
      // Error updating menu status silently
      const revertedMenu = menu.map((item) =>
        item.menu_ID === Menu_ID
          ? { ...item, menu_Publish: currentStatus }
          : item
      );
      setMenu(revertedMenu);
      showErrorToast("Failed to update menu status.");
    } finally {
      setLoading(false);
    }
  };

  const renderNoDataMessage = () => {
    if (search.trim() && menu.length === 0) {
      return (
        <div className="alert alert-info text-center">
          No menu items found matching your search criteria.
        </div>
      );
    }
    return null;
  };

  return (
    <div
      style={{
        position: "relative",
        minHeight: "100vh",
        paddingBottom: selectedItems.size > 0 ? "100px" : "0",
      }}
    >
      {loading && <Preloader />}
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <a href="#/">Home</a>
            </li>
            <li className="breadcrumb-item active">Manage Food Menu</li>
          </ol>
          <h1 className="page-header mb-0">Manage Food Menu</h1>
        </div>
        
        <div className="ms-auto">
          {!isChildBranch && (
            <button
              onClick={handleAddMenu}
              className="btn btn-primary px-4"
              style={{
                backgroundColor: themeColor,
                background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                border: `1px solid ${themeColor}`,
                color: 'white',
                borderRadius: '8px',
                padding: '8px 20px',
                fontSize: '14px',
                fontWeight: '600',
                transition: 'all 0.3s ease',
                boxShadow: `0 2px 8px ${themeColor}40`
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = `0 4px 12px ${themeColor}60`;
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = `0 2px 8px ${themeColor}40`;
              }}
            >
              <i className="fa fa-plus me-2"></i>
              Add Menu
            </button>
          )}
        </div>
      </div>

      <div className="mb-3 mt-2">
        <div className="card border-0">
          <div className="tab-content p-3">
            <div className="tab-pane fade show active" id="allTab">
              {/* Enhanced Filter Section */}
              <div className="mb-4">
                <div
                  className="card filter-card"
                  style={{
                    position: "relative",
                    zIndex: 1,
                    background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
                    borderRadius: '12px',
                    border: 'none',
                    boxShadow: `0 4px 20px ${themeColor}40`
                  }}
                >
                  <div className="card-body p-3">
                    {/* Filter Controls */}
                    <div className="row g-3 align-items-center">
                      {/* Search Field */}
                      <div className="col-xl-4 col-lg-4 col-md-6">
                        <div className="search-input filter-control">
                          <input
                            type="text"
                            className="filter-input"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search by menu name..."
                            style={{
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              border: '1px solid rgba(255, 255, 255, 0.3)',
                              color: '#333'
                            }}
                          />
                        </div>
                      </div>

                      {/* Sort By Dropdown */}
                      <div className="col-xl-3 col-lg-3 col-md-6">
                        <div className="filter-control">
                          <Select
                            value={sortBy}
                            onChange={(val) => dispatch(setSortBy({ pageKey, sortBy: val }))}
                            options={sortByOptions}
                            styles={dropdownStyles}
                            placeholder="Sort By"
                            className="filter-dropdown"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            menuShouldBlockScroll={false}
                            menuShouldScrollIntoView={false}
                            closeMenuOnScroll={false}
                          />
                        </div>
                      </div>

                      {/* Sort Order Dropdown */}
                      <div className="col-xl-2 col-lg-2 col-md-6">
                        <div className="filter-control">
                          <SortOrderDropDownSelection
                            defaultValue={sortOrder}
                            onChange={(val) => dispatch(setSortOrder({ pageKey, sortOrder: val }))}
                          />
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="col-xl-3 col-lg-3 col-md-12">
                        <div className="d-flex gap-2">
                          <button
                            className="clear-btn"
                            onClick={clearFilters}
                            title="Clear all filters"
                          >
                            <i className="fa fa-times me-1"></i>
                            Filter
                          </button>

                          <ColumnSelection
                            visibleColumns={visibleColumns}
                            setVisibleColumns={(cols) =>
                              dispatch(setVisibleColumns({ pageKey, columns: cols }))
                            }
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Selection Info and Actions (Shown only when menus are selected) */}
              {selectedItems.size > 0 && (
                <div className="mb-3">
                  <div className="p-3 bg-light rounded d-flex align-items-center justify-content-between" style={{ border: "1px solid #dee2e6" }}>
                    <div className="d-flex align-items-center gap-3">
                      <span className="fw-bold text-primary">
                        {selectedItems.size} menu{selectedItems.size > 1 ? 's' : ''} selected
                      </span>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={handleDeselectAll}
                      >
                        Clear Selection
                      </button>
                    </div>

                    <div className="d-flex gap-2">
                      {!isChildBranch && (
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={handleBulkEdit}
                          title="Edit Selected Menus"
                        >
                          <i className="fa fa-edit me-1"></i>
                          Edit
                        </button>
                      )}
                      <button
                        className="btn btn-sm btn-success"
                        onClick={() => handleBulkStatusChange(true)}
                        title="Publish Selected Menus"
                      >
                        <i className="fa fa-check-circle me-1"></i>
                        Publish
                      </button>
                      <button
                        className="btn btn-sm btn-warning"
                        onClick={() => handleBulkStatusChange(false)}
                        title="Unpublish Selected Menus"
                      >
                        <i className="fa fa-times-circle me-1"></i>
                        Unpublish
                      </button>
                      <button
                        className="btn btn-sm btn-danger"
                        onClick={handleBulkDelete}
                        title="Delete Selected Menus"
                      >
                        <i className="fa fa-trash me-1"></i>
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {renderNoDataMessage()}

              <div
                className="table-responsive mt-3"
                style={{
                  minHeight: "200px",
                  maxHeight: "450px",
                  overflowY: "auto",
                  overflowX: "auto",
                }}
              >
                <table className="table table-hover">
                  <thead
                    className="bg-light"
                    style={{
                      position: "sticky",
                      top: 0,
                      zIndex: 5,
                      backgroundColor: isDarkMode ? "#2d3436" : "#f8f9fa",
                      color: isDarkMode ? "#ffffff" : "#000000",
                    }}
                  >
                    <tr>
                      <th className="pt-2 pb-2">
                        <input
                          type="checkbox"
                          onChange={handleSelectAll}
                          checked={
                            menu.length > 0 &&
                            selectedItems.size === menu.length
                          }
                        />
                      </th>
                      <th className="pt-2 pb-2">Menu Name</th>
                      {visibleColumns.sortOrder && (
                        <th className="pt-2 pb-2 text-center">
                          Sort Order
                        </th>
                      )}
                      {visibleColumns.status && (
                        <th className="pt-2 pb-2 text-center">Status</th>
                      )}
                      <th className="pt-2 pb-2 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {menu.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="text-center py-4">
                          {search.trim() ? "No menu items found matching your search criteria." : "No menu items available."}
                        </td>
                      </tr>
                    ) : (
                      menu.map((menu, index) => (
                        <tr
                          key={menu.menu_ID}
                          draggable={false}
                          onDragStart={(e) => {
                            // Only allow dragging from the grab icon
                            if (!e.target.classList.contains('fa-bars')) {
                              e.preventDefault();
                            }
                          }}
                          onDragEnter={(e) => handleDragEnter(e, index)}
                          onDragLeave={handleDragLeave}
                          onDragOver={handleDragOver}
                          onDrop={handleDrop}
                        >
                          <td className="align-middle">
                            <input
                              type="checkbox"
                              checked={selectedItems.has(menu.menu_ID)}
                              onChange={(e) =>
                                handleSelectItem(menu.menu_ID, index, e)
                              }
                              data-chkbox-shiftsel="type1"
                            />
                          </td>
                          <td
                            className="align-middle"
                            onClick={() => {
                              if (isChildBranch) return;
                              navigate(`/Food-menu/updateFoodMenu/${menu.menu_ID}`);
                            }}
                            style={{ cursor: isChildBranch ? "default" : "pointer" }}
                          >
                            {menu.menu_Name}
                          </td>
                          {visibleColumns.sortOrder && (
                            <td
                              className="align-middle text-center"
                              onClick={() => {
                                if (isChildBranch) return;
                                navigate(`/Food-menu/updateFoodMenu/${menu.menu_ID}`);
                              }}
                              style={{ cursor: isChildBranch ? "default" : "pointer" }}
                            >
                              {menu.menu_Orderby}
                            </td>
                          )}
                          {visibleColumns.status && (
                            <td className="align-middle text-center">
                              <div
                                className={`toggle-switch ${menu.menu_Publish ? "active" : ""
                                  }`}
                              >
                                <label className="switch">
                                  <input
                                    type="checkbox"
                                    checked={menu.menu_Publish}
                                    onChange={() =>
                                      toggleMenuStatus(
                                        menu.menu_ID,
                                        menu.menu_Publish
                                      )
                                    }
                                  />
                                  <span
                                    className="slider round"
                                    style={{
                                      backgroundColor: menu.menu_Publish ? themeColor : '#ccc'
                                    }}
                                  ></span>
                                </label>
                              </div>
                            </td>
                          )}
                          <td className="align-middle text-center">
                            <div className="d-flex justify-content-center gap-2">
                              {/* Drag Handle */}
                              <i
                                className="fas fa-bars text-secondary"
                                style={{
                                  cursor: "grab",
                                  width: '32px',
                                  height: '32px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderRadius: '6px',
                                  border: '1px solid #6c757d',
                                  backgroundColor: 'transparent',
                                  transition: 'all 0.2s ease'
                                }}
                                draggable
                                onDragStart={(e) => handleDragStart(e, index)}
                                onDragEnd={handleDragEnd}
                                title="Drag to reorder"
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#6c757d';
                                  e.currentTarget.style.color = 'white';
                                  e.currentTarget.style.transform = 'scale(1.05)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                  e.currentTarget.style.color = '#6c757d';
                                  e.currentTarget.style.transform = 'scale(1)';
                                }}
                              ></i>

                              {/* Edit Icon */}
                              {!isChildBranch && (
                                <Link
                                  to={`/Food-menu/updateFoodMenu/${menu.menu_ID}`}
                                  className="btn btn-sm btn-outline-primary"
                                  title="Edit Menu"
                                  style={{
                                    width: '32px',
                                    height: '32px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    borderRadius: '6px',
                                    border: `1px solid ${themeColor}`,
                                    color: themeColor,
                                    transition: 'all 0.2s ease'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = themeColor;
                                    e.currentTarget.style.color = 'white';
                                    e.currentTarget.style.transform = 'scale(1.05)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'transparent';
                                    e.currentTarget.style.color = themeColor;
                                    e.currentTarget.style.transform = 'scale(1)';
                                  }}
                                >
                                  <i className="fa fa-edit" style={{ fontSize: '12px' }}></i>
                                </Link>
                              )}

                              {/* Delete Icon */}
                              <button
                                onClick={() => handleDeleteMenu(menu)}
                                className="btn btn-sm btn-outline-danger"
                                title="Delete Menu"
                                style={{
                                  width: '32px',
                                  height: '32px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  borderRadius: '6px',
                                  border: '1px solid #dc3545',
                                  color: '#dc3545',
                                  transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#dc3545';
                                  e.currentTarget.style.color = 'white';
                                  e.currentTarget.style.transform = 'scale(1.05)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                  e.currentTarget.style.color = '#dc3545';
                                  e.currentTarget.style.transform = 'scale(1)';
                                }}
                              >
                                <i className="fa fa-trash" style={{ fontSize: '12px' }}></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

  );
};

export default ManageMenus;